
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import ShowProfitToggle from '@/components/ShowProfitToggle.vue';
import settingx from '@/store/modules/settingx';
import { roundToFixed } from '@/util/number';
import gdbx from '@/store/modules/gdbx';
import { COL, sqlSum, sqlSumByDocType, getTableName } from '@/worker/db/sqlHelpers';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';

@Component({
	components: {
        IncludeDebitNoteToggle,
		AmountDisplayerFilterable,
		ShowProfitToggle,
		IncludeCreditNoteToggle,
        SlPhModuleOnlyToggle,
	},
})
export default class AgentYtdSalesProfitNumberDisplayer extends FilterWidget {
	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public profitPercentage = 0;
	public profitAmount = '' || 0;

	public get permissionIds(): PermissionsGroup[] {
		return ['sales'];
	}

	public get showProfit() {
		return settingx.now.showProfit;
	}

	public get title() {
		return this.showProfit ? 'YTD' + ' Sales Profit' : 'YTD' + ' Sales Margin';
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

	public get selectedYearToDateRange(): [number, number] {
		return [
			moment(this.selectedAsOfDate).startOf('year').valueOf(),
			moment(this.selectedAsOfDate).endOf('month').valueOf(),
		];
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedYearToDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedYearToDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		const YTDThisYear = this.selectedYearToDateRange;

		const queryHead = await this._ensureTablesLoaded(
			['sales', 'creditNotes', 'customerTrades'] as any[],
		);
		this.checkInterrupted(jobId);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');

		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${YTDThisYear[0]} AND ${YTDThisYear[1]}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		// SL amounts and profits (always needed)
		const salesDocTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
		const slDocTypeFilter = ` AND ${COL.DOCTYPE} IN (${salesDocTypes.map((t) => `'${t}'`).join(', ')})`;

		const [slSalesRows, slCNRows] = await Promise.all([
			queryHead.rawQuery(`
				SELECT ${sqlSum(COL.AMOUNT, 'totalAmt')}, ${sqlSum(COL.PROFIT, 'totalProfit')}
				FROM ${salesTable}
				WHERE ${dateFilter}${agentFilter}${slDocTypeFilter}
			`),
			queryHead.rawQuery(`
				SELECT ${sqlSum(COL.AMOUNT, 'totalAmt')}, ${sqlSum(COL.PROFIT, 'totalProfit')}
				FROM ${cnTable}
				WHERE ${dateFilter}${agentFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		const slSalesAmount = slSalesRows[0] ? Number(slSalesRows[0].totalAmt) || 0 : 0;
		const slSalesProfit = slSalesRows[0] ? Number(slSalesRows[0].totalProfit) || 0 : 0;
		const slCNAmount = slCNRows[0] ? Number(slCNRows[0].totalAmt) || 0 : 0;
		const slCNProfit = slCNRows[0] ? Number(slCNRows[0].totalProfit) || 0 : 0;

		const slProfit = slSalesProfit - (this.includeCreditNote ? slCNProfit : 0);
		const slAmount = slSalesAmount - (this.includeCreditNote ? slCNAmount : 0);

		let salesAmount: number;
		let profitAmount: number;

		if (this.slPhModuleOnly) {
			salesAmount = slSalesAmount;
			profitAmount = slProfit;
		} else {
			const arRows = await queryHead.rawQuery(`
				SELECT
					${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
					${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
					${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
				FROM ${arTable}
				WHERE ${dateFilter}${agentFilter}
			`);
			this.checkInterrupted(jobId);

			const arIv = arRows[0] ? Number(arRows[0].iv_amount) || 0 : 0;
			const arCN = arRows[0] ? Number(arRows[0].cn_amount) || 0 : 0;
			const arDN = arRows[0] ? Number(arRows[0].dn_amount) || 0 : 0;

			const arAmount = arIv
				- (this.includeCreditNote ? arCN : 0)
				+ (this.includeDebitNote ? arDN : 0);

			const extraProfit = arAmount - slAmount;

			salesAmount = arAmount;
			profitAmount = slProfit + extraProfit;
		}

		this.profitAmount = profitAmount;

		this.profitPercentage = roundToFixed(
			salesAmount !== 0 ? (this.profitAmount / salesAmount) * 100 : 0,
			this.decimal,
		);

		this.saveHistory('profitAmount', 'profitPercentage');
	}
}
